import React, { useEffect, useState, useRef } from 'react';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import ExportButton from '../components/ExportButton';
import { buildNewListenersDoc } from "../pdf/builders/new_listeners/buildDoc";
import { buildNewListenersWorkbook } from "../excel/builders/new_listeners/buildWorkbook";
import { resolveCountryFlag } from "../constants/countries";

export default function ReachServicePage() {
  // период: День / Тиждень / Місяць
  const [period, setPeriod] = useState('day');    // 'day' | 'week' | 'month'
  const [date1, setDate1] = useState('');
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [total, setTotal] = useState(0);          // Reach за период
  const [info, setInfo] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [geo, setGeo] = useState([]);             // зарезервировано для будущего GEO

  const chartRef = useRef(null);
  const exportBlockRef = useRef(null);
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t } = useTranslation('common');
  const locale = useLocale();

  // по умолчанию — вчера; если выбрали сегодня, тоже смещаем на вчера
  useEffect(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    if (!date1 || date1 === todayStr) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);       // берём всегда прошедший день
      setDate1(yesterday.toISOString().slice(0, 10));
    }
  }, [date1]);

  const calcRange = () => {
    if (!date1) return null;
    const endDate = new Date(date1);
    if (Number.isNaN(endDate.getTime())) return null;

    const from = new Date(endDate);

    if (period === 'day') {
      // один день: от и до — один и тот же день
      // from уже == endDate, ничего не меняем
    } else if (period === 'week') {
      // 7 дней: выбранный день + 6 назад
      from.setDate(endDate.getDate() - 6);
    } else if (period === 'month') {
      // ~1 месяц: как в ADR — месяц назад
      from.setMonth(endDate.getMonth() - 1);
    }

    const dateFromStr = from.toISOString().slice(0, 10);
    const dateToStr = endDate.toISOString().slice(0, 10);
    return { dateFromStr, dateToStr };
  };

  const fetchData = async () => {
    if (!isAuth || !radioId || !date1) return;

    const range = calcRange();
    if (!range) {
      setInfo(t('common.invalid_date') || 'Invalid date');
      return;
    }

    const { dateFromStr, dateToStr } = range;

    setInfo(t('common.loading'));
    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/reach/summary?date_from=${dateFromStr}&date_to=${dateToStr}`;
      const resp = await authFetch(url);
      const json = await resp.json();

      if (!resp.ok || json.error || json.detail) {
        setInfo(json.error || json.detail || `Error: ${resp.status}`);
        setData([]); setLabels([]); setGeo([]); setTotal(0);
        setStart(''); setEnd('');
        return;
      }

      // для Reach бекенд отдает одно число и (опционально) GEO
      const reachValue =
        typeof json.reach === 'number'
          ? json.reach
          : typeof json.reach_total === 'number'
            ? json.reach_total
            : (Array.isArray(json.items) && json.items.length
                ? json.items[0].value || 0
                : 0);

      setData([reachValue]);           // чтобы экспорт работал как раньше
      setLabels([`${json.date_from} — ${json.date_to}`]);
      setTotal(reachValue);
      let geoArr = [];
      if (Array.isArray(json.geo)) {
        geoArr = json.geo;
      } else if (typeof json.geo === 'string') {
        try {
          const parsed = JSON.parse(json.geo);
          if (Array.isArray(parsed)) {
            geoArr = parsed;
          }
        } catch (e) {
          // игнорируем, оставляем geoArr = []
        }
      }
      setGeo(geoArr);
      setStart(json.date_from || '');
      setEnd(json.date_to || '');
      setInfo('');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Reach fetch error', e);
      setInfo(t('common.network_error'));
      setData([]); setLabels([]); setGeo([]); setTotal(0);
      setStart(''); setEnd('');
    }
  };

  const clean = (value) => String(value ?? '').replace(/[^0-9A-Za-z_-]/g, '-');
  const filenameBase = `reach_${clean(shortcode || 'channel')}_${clean(period || 'period')}_${clean(date1 || '')}`;
  const exportDisabled = !data?.length || !!info;
  const exportButtonStyle = calcExportButtonStyle(exportDisabled);
  const geoForExport = (geo || []).map((row) => {
    const value = row.listeners_count ?? row.listeners ?? row.cnt ?? 0;
    return {
      ...row,
      listeners: value,
      cnt: value
    };
  });

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>{t('reach.title') || 'Reach'}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('common.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* Описание сервиса */}
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 16.5,
          margin: "8px 0 20px 0",
          boxSizing: "border-box",
          whiteSpace: "pre-line",
          lineHeight: 1.65
        }}
      >
        {t('reach.desc') ||
          'Reach показує кількість унікальних слухачів за обраний період (день, тиждень, місяць).'}
      </div>

      <h2 style={{ marginBottom: 8 }}>{t('reach.title') || 'Reach'}</h2>

      {/* Період + дата */}
      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: PERIOD_BUTTON_GAP
        }}
      >
        <div style={{ display: 'inline-flex', gap: PERIOD_BUTTON_GAP }}>
          <button onClick={() => setPeriod('day')} style={periodBtn(period === 'day')}>
            {t('reach.period.day', 'День')}
          </button>
          <button onClick={() => setPeriod('week')} style={periodBtn(period === 'week')}>
            {t('reach.period.week', 'Тиждень')}
          </button>
          <button onClick={() => setPeriod('month')} style={periodBtn(period === 'month')}>
            {t('reach.period.month', 'Місяць')}
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: DATE_FIELD_GAP,
            marginLeft: BUTTONS_DATE_SPACING,
            flexShrink: 0,
            minWidth: DATE_BLOCK_MIN_WIDTH
          }}
        >
          <span style={{ minWidth: DATE_LABEL_MIN_WIDTH, fontWeight: 500, textAlign: 'right' }}>
            {period === 'day' ? t('tsl.date_label_day') : t('tsl.date_label_last')}
          </span>
          <input type="date" value={date1} onChange={e => setDate1(e.target.value)} style={inputStyle} />
        </div>
      </div>

      {/* Кнопки показать/экспорт */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          marginBottom: 16
        }}
      >
        <button
          type="button"
          onClick={fetchData}
          style={showButtonStyle}
        >
          {t('common.show')}
        </button>

        <ExportButton
          data={data}
          onBeforeBuild={async () => {
            const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
            let logoDataUrl = null;
            try {
              const resp = await fetch(logoUrl, { cache: 'no-store' });
              const blob = await resp.blob();
              const reader = new FileReader();
              logoDataUrl = await new Promise((res, rej) => {
                reader.onloadend = () => res(reader.result);
                reader.onerror = rej;
                reader.readAsDataURL(blob);
              });
            } catch {}
            const chartDataUrl =
              (chartRef.current && chartRef.current.toDataURL && chartRef.current.toDataURL("image/png")) || null;
            return { logoDataUrl, chartDataUrl };
          }}
          buildDoc={(payload) =>
            buildNewListenersDoc({
              t, shortcode, stationName,
              serviceName: t('reach.title') || 'Reach',
              period, date1,
              start, end, total, labels, data, geo: geoForExport,
              fmtNumber, locale,
              logoDataUrl: payload?.logoDataUrl || null,
              chartDataUrl: payload?.chartDataUrl || null
            })
          }
          buildWorkbook={(XLSX) =>
            buildNewListenersWorkbook(XLSX, {
              t, shortcode, stationName,
              serviceName: t('reach.title') || 'Reach',
              period, date1,
              start, end, total, labels, data, geo: geoForExport,
              fmtNumber, locale
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>

      {/* Результаты + график */}
      <div ref={exportBlockRef}>
        {info && (
          <div style={{ fontWeight: 500, margin: "18px 0", color: 'red' }}>
            {info}
          </div>
        )}
        {!info && (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
              {total > 0 ? (
                <>
                  {t('reach.total') || 'Reach за період'}:
                  {' '}
                  <span style={{ fontSize: 28, lineHeight: 1, display: 'inline-block', marginLeft: 4 }}>
                    {fmtNumber(total, locale)}
                  </span>
                  <br />
                  {(t('reach.period_range') || t('new_listeners.period'))}: {start} — {end}
                </>
              ) : (
                t('common.no_data')
              )}
            </div>

            {total > 0 && geo && geo.length > 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  background: '#fafafa'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {t('reach.top_countries') || 'Топ-10 країн за Reach'}
                </div>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 14
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #ddd' }}>
                        {t('reach.country') || 'Країна'}
                      </th>
                      <th style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid #ddd' }}>
                        {t('reach.listeners') || 'Слухачі'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {geo.slice(0, 10).map((row, idx) => {
                      const flag = resolveCountryFlag(row);
                      return (
                        <tr key={`${row.country_code || row.country || 'cc'}-${idx}`}>
                          <td style={{ padding: '4px 6px', borderBottom: '1px solid #eee' }}>
                            {flag ? (
                              <span>
                                {flag}{' '}
                              </span>
                            ) : null}
                            {row.country || row.country_code || '—'}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                            {fmtNumber(row.listeners_count || row.listeners || 0, locale)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {total > 0 && (!geo || geo.length === 0) && (
              <div style={{ color: '#777', fontSize: 14, marginTop: 8 }}>
                {t('reach.no_geo') || 'Дані по країнах для цього періоду відсутні.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const widgetStyle = {
  maxWidth: 1020,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const inputStyle = {
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130
};

const PERIOD_BUTTON_WIDTH = 110;
const periodBtn = active => ({
  background: active ? '#36a2eb' : '#f4f4fa',
  color: active ? '#fff' : '#222',
  border: 'none',
  padding: '7px 18px',
  borderRadius: 5,
  fontWeight: 600,
  cursor: 'pointer',
  width: PERIOD_BUTTON_WIDTH
});

const ACTION_BUTTON_WIDTH = 218;
const ACTION_BUTTON_GAP = 10;
const PERIOD_BUTTON_GAP = 8;
const DATE_FIELD_GAP = 8;
const BUTTONS_DATE_SPACING = -6;
const DATE_BLOCK_MIN_WIDTH = 260;
const DATE_LABEL_MIN_WIDTH = 160;

const baseActionButtonStyle = {
  ...periodBtn(false),
  width: ACTION_BUTTON_WIDTH
};

const showButtonStyle = {
  ...baseActionButtonStyle,
  background: '#3867fa',
  color: '#fff'
};

const calcExportButtonStyle = (disabled) => ({
  ...baseActionButtonStyle,
  background: disabled ? '#9bbdfd' : '#3867fa',
  color: '#fff',
  cursor: disabled ? 'not-allowed' : baseActionButtonStyle.cursor
});
